import React from 'react';
import { onSnapshot, doc, collection, getDocs } from 'firebase/firestore';
import { setDoc } from './api/FirestoreInterceptor';
import { db } from './config-firebase/firebase';

export const GlobalContext = React.createContext();

export const GlobalStorage = ({ children }) => {
  const [image, setImage] = React.useState('');
  const [bestSellersImage, setBestSellersImage] = React.useState('');
  const [id, setId] = React.useState('');
  const [isToten, setIsToten] = React.useState(() => {
    return JSON.parse(localStorage.getItem('isToten') || 'false');
  });
  const [authorizated, setAuthorizated] = React.useState(false);
  const [userNewRequest, setUserNewRequest] = React.useState({});
  const [pdvRequest, setPdvRequest] = React.useState(false);
  const [orderBeingEdited, setOrderBeingEdited] = React.useState(null);
  const [warningLowRawMaterial, setWarningLowRawMaterial] = React.useState([]);
  const [enableAutoNfce, setEnableAutoNfce] = React.useState(false);
  const [styles, setStyles] = React.useState({
    btnColor: '#b02121',
    secundaryBgColor: '#b02121',
    bgColor: '#b02121',
    fontColor: 'rgb(230, 235, 230)',
    titleFontColor: '#bd2828',
    titleFont: 'Arial',
    textFont: 'sans serif',
  });

  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [packageTier, setPackageTier] = React.useState(1);
  const [hasClients, setHasClients] = React.useState(false);
  const [hasRawMaterial, setHasRawMaterial] = React.useState(false);
  const [hasFinancial, setHasFinancial] = React.useState(false);
  const [canConfigToten, setCanConfigToten] = React.useState(false);
  const [maxDeliveryDistance, setMaxDeliveryDistance] = React.useState(1);
  const [establishmentCep, setEstablishmentCep] = React.useState('');
  const [establishmentCoords, setEstablishmentCoords] = React.useState(null);
  const [serviceChargeActive, setServiceChargeActive] = React.useState(false);
  const [serviceChargePercent, setServiceChargePercent] = React.useState(10);
  const [allowKitchenDelivery, setAllowKitchenDelivery] = React.useState(() => {
    return JSON.parse(localStorage.getItem('allowKitchenDelivery') || 'false');
  });
  const [canSelectKitchenDishes, setCanSelectKitchenDishes] = React.useState(() => {
    return JSON.parse(localStorage.getItem('canSelectKitchenDishes') || 'false');
  });
  const [billClosingMessage, setBillClosingMessage] = React.useState({
    primary: 'O Garçom já está a caminho com a máquina para finalizar o seu pagamento na mesa.',
    secondary: 'Por favor, aguarde o fechamento da conta.'
  });
  const [billClosingConfig, setBillClosingConfig] = React.useState({
    option: 'default',
    customMessage: ''
  });
  const [pdvMobileView, setPdvMobileView] = React.useState('menu'); // 'menu' ou 'orders'
  const [currentUser, setCurrentUser] = React.useState(null);
  const [currentUserEmail, setCurrentUserEmail] = React.useState(() => {
    return localStorage.getItem('currentUserEmail') || '';
  });

  const loginUser = (email, token) => {
    localStorage.setItem('token', JSON.stringify(token));
    localStorage.setItem('currentUserEmail', email);
    setCurrentUserEmail(email);
  };

  const logoutUser = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('currentUserEmail');
    localStorage.removeItem('currentUserName');
    localStorage.removeItem('currentUserRole');
    localStorage.removeItem('currentUserPermissions');
    localStorage.removeItem('pdv');
    localStorage.removeItem('isToten');
    setPdvRequest(false);
    setIsToten(false);
    setCurrentUserEmail('');
    setCurrentUser(null);
  };

  // Listen for global configuration changes
  React.useEffect(() => {
    if (!db) {
      console.warn('Firestore db instance not available in GlobalContext');
      return;
    }
    const unsubscribeNfc = onSnapshot(doc(db, 'GlobalConfig', 'nfcSettings'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.enableAutoNfce !== undefined) {
          setEnableAutoNfce(data.enableAutoNfce);
          localStorage.setItem('enableAutoNfce', JSON.stringify(data.enableAutoNfce));
        }
      }
    });

    const unsubscribePackage = onSnapshot(doc(db, 'GlobalConfig', 'packageSettings'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.packageTier !== undefined) {
          setPackageTier(Number(data.packageTier));
        }
        setHasClients(!!data.hasClients);
        setHasRawMaterial(!!data.hasRawMaterial);
        setHasFinancial(!!data.hasFinancial);
        setCanConfigToten(!!data.canConfigToten);
      }
    });

    const unsubscribeDelivery = onSnapshot(doc(db, 'GlobalConfig', 'deliverySettings'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.maxDeliveryDistance !== undefined) {
          setMaxDeliveryDistance(Number(data.maxDeliveryDistance));
        }
        if (data.establishmentCep !== undefined) {
          setEstablishmentCep(data.establishmentCep);
        }
        if (data.establishmentCoords !== undefined) {
          setEstablishmentCoords(data.establishmentCoords);
        }
      }
    });

    const unsubscribeFrontImage = onSnapshot(doc(db, 'frontImage', 'oIKq1AHF4cHMkqgOcz1h'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.image) {
          setImage(data.image);
        }
        if (data.bestSellersImage !== undefined) {
          setBestSellersImage(data.bestSellersImage);
        }
      }
    }, (error) => {
      console.warn('Error listening to frontImage:', error);
    });

    const unsubscribeServiceSettings = onSnapshot(doc(db, 'GlobalConfig', 'serviceSettings'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.serviceChargeActive !== undefined) {
          setServiceChargeActive(Boolean(data.serviceChargeActive));
        }
        if (data.serviceChargePercent !== undefined) {
          setServiceChargePercent(Number(data.serviceChargePercent));
        }
      }
    }, (error) => {
      console.warn('Error listening to serviceSettings:', error);
    });

    const unsubscribeKitchenSettings = onSnapshot(doc(db, 'GlobalConfig', 'kitchenSettings'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.allowKitchenDelivery !== undefined) {
          setAllowKitchenDelivery(Boolean(data.allowKitchenDelivery));
          localStorage.setItem('allowKitchenDelivery', JSON.stringify(Boolean(data.allowKitchenDelivery)));
        }
      }
    }, (error) => {
      console.warn('Error listening to kitchenSettings:', error);
    });

    const unsubscribeWelcome = onSnapshot(doc(db, 'welcomeCustomer', 'Zju4GZbnQFaq4fWjGch1'), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const option = data.billClosingOption || 'default';
        const customMsg = data.customBillClosingMessage || '';
        setBillClosingConfig({
          option,
          customMessage: customMsg
        });

        if (option === 'cashier') {
          setBillClosingMessage({
            primary: 'Por favor, dirija-se ao caixa para realizar o pagamento e informe o seu nome e o número da sua mesa.',
            secondary: 'Por favor, aguarde o atendimento no caixa.'
          });
        } else if (option === 'custom' && customMsg.trim()) {
          setBillClosingMessage({
            primary: customMsg.trim(),
            secondary: ''
          });
        } else {
          setBillClosingMessage({
            primary: 'O Garçom já está a caminho com a máquina para finalizar o seu pagamento na mesa.',
            secondary: 'Por favor, aguarde o fechamento da conta.'
          });
        }
      }
    }, (error) => {
      console.warn('Error listening to welcomeCustomer:', error);
    });

    return () => {
      unsubscribeNfc();
      unsubscribePackage();
      unsubscribeDelivery();
      unsubscribeFrontImage();
      unsubscribeServiceSettings();
      unsubscribeKitchenSettings();
      unsubscribeWelcome();
    };
  }, []);

  // Pre-cachear imagens localmente para funcionamento rápido e offline
  React.useEffect(() => {
    const toten = JSON.parse(localStorage.getItem('isToten') || 'false');
    if (toten && db) {
      import('./util/imageCache').then((m) => {
        m.precacheAllImages(db);
      });
    }
  }, [db]);

  // [NOVO] Ref global para evitar disparos duplicados de NFC-e em toda a aplicação
  const processedOrdersGlobal = React.useRef(new Set());



  // Listen to current logged-in user changes in Firestore
  React.useEffect(() => {
    if (!db || !currentUserEmail) {
      setCurrentUser(null);
      return;
    }
    const emailClean = currentUserEmail.trim().toLowerCase();
    const unsub = onSnapshot(doc(db, 'admins', emailClean), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        setCurrentUser(data);
        localStorage.setItem('currentUserName', data.name || '');
        localStorage.setItem('currentUserRole', data.role || '');
        localStorage.setItem('currentUserPermissions', JSON.stringify(data.permissions || []));

        // Auto-ativação do modo PDV para garçons e operadores restritos
        const isPdvStaff = data.role !== 'admin_TOTAL' &&
                           Array.isArray(data.permissions) &&
                           data.permissions.includes('requestlist') &&
                           !data.permissions.includes('frontimage');

        if (isPdvStaff) {
          localStorage.setItem('pdv', 'true');
          localStorage.setItem('isToten', 'false');
          setPdvRequest(true);
          setIsToten(false);
        }
      } else {
        // Autonomia total: se não existir nenhum Super Admin no banco, o usuário logado vira Super Admin!
        getDocs(collection(db, 'admins')).then(snapshot => {
          const hasAdminTotal = snapshot.docs.some(d => d.data()?.role === 'admin_TOTAL');
          const isSuperAdmin = !hasAdminTotal;
          const tempUser = {
            email: emailClean,
            name: emailClean.split('@')[0],
            role: isSuperAdmin ? 'admin_TOTAL' : 'custom',
            permissions: isSuperAdmin ? ['ALL'] : ['requestlist']
          };
          
          // Salva automaticamente o novo usuário no banco para que ele apareça na tabela de Gerência de Acessos
          setDoc(doc(db, 'admins', emailClean), tempUser).catch(e => console.error('Erro ao auto-registrar admin', e));
          
          setCurrentUser(tempUser);
          localStorage.setItem('currentUserName', tempUser.name);
          localStorage.setItem('currentUserRole', tempUser.role);
          localStorage.setItem('currentUserPermissions', JSON.stringify(tempUser.permissions || []));

          if (!isSuperAdmin) {
            localStorage.setItem('pdv', 'true');
            localStorage.setItem('isToten', 'false');
            setPdvRequest(true);
            setIsToten(false);
          }
        });
      }
    }, (err) => {
      console.error('[RBAC] Erro ao monitorar permissões do usuário logado:', err);
    });
    return () => unsub();
  }, [db, currentUserEmail]);

  return (
    <GlobalContext.Provider
      value={{
        image,
        setImage,
        bestSellersImage,
        setBestSellersImage,
        warningLowRawMaterial,
        setWarningLowRawMaterial,
        id,
        setId,
        styles,
        setStyles,
        authorizated,
        setAuthorizated,
        setIsToten,
        isToten,
        userNewRequest,
        setUserNewRequest,
        setPdvRequest,
        pdvRequest,
        orderBeingEdited,
        setOrderBeingEdited,
        processedOrdersGlobal,
        enableAutoNfce,
        setEnableAutoNfce,
        isInitialLoad,
        setIsInitialLoad,
        packageTier,
        hasClients,
        hasRawMaterial,
        hasFinancial,
        canConfigToten,
        setPackageTier,
        pdvMobileView,
        setPdvMobileView,
        maxDeliveryDistance,
        establishmentCep,
        establishmentCoords,
        serviceChargeActive,
        setServiceChargeActive,
        serviceChargePercent,
        setServiceChargePercent,
        allowKitchenDelivery,
        setAllowKitchenDelivery,
        canSelectKitchenDishes,
        setCanSelectKitchenDishes,
        billClosingMessage,
        setBillClosingMessage,
        billClosingConfig,
        setBillClosingConfig,
        currentUser,
        setCurrentUser,
        currentUserEmail,
        loginUser,
        logoutUser,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};
